import * as THREE from 'three';
import { useMemo, useRef, useLayoutEffect } from 'react';
import { useControls, folder } from 'leva';
import { isRoad, isRiver, isObject, getHeight } from './Terrain';
import { isMobile } from '../../utils/isMobile';

// Grass Types Definition
const GRASS_TYPES = [
    { name: 'Tall', width: 0.1, height: 1.2, segments: 3, colorBot: [0.1, 0.4, 0.1], colorTop: [0.4, 0.8, 0.2], countVertices: 12000 },
    { name: 'Short', width: 0.15, height: 0.6, segments: 2, colorBot: [0.2, 0.5, 0.1], colorTop: [0.6, 0.9, 0.3], countVertices: 8000 },
    { name: 'Wild', width: 0.3, height: 0.9, segments: 2, colorBot: [0.3, 0.4, 0.1], colorTop: [0.8, 0.8, 0.4], countVertices: 4000 },
] as const;

// Chunking Configuration
const MAP_SIZE = 400; // -200 to 200
const CHUNK_SIZE = 50;
const CHUNKS_PER_ROW = MAP_SIZE / CHUNK_SIZE;

export const GrassField = ({ lowPerf = false }: { lowPerf?: boolean }) => {
    // Generate Chunks
    const chunks = useMemo(() => {
        const c = [];
        for (let x = -MAP_SIZE / 2; x < MAP_SIZE / 2; x += CHUNK_SIZE) {
            for (let z = -MAP_SIZE / 2; z < MAP_SIZE / 2; z += CHUNK_SIZE) {
                c.push({ x, z });
            }
        }
        return c;
    }, []);

    const mobile = isMobile();

    // Leva Controls
    const {
        tallCount, tallWidth, tallHeight,
        shortCount, shortWidth, shortHeight,
        wildCount, wildWidth, wildHeight
    } = useControls('Grass', {
        'Tall Grass': folder({
            tallCount: { value: 12000, min: 1000, max: 50000, step: 1000 },
            tallWidth: { value: 0.1, min: 0.05, max: 0.5, step: 0.01 },
            tallHeight: { value: 1.2, min: 0.5, max: 3, step: 0.1 },
        }),
        'Short Grass': folder({
            shortCount: { value: 8000, min: 1000, max: 50000, step: 1000 },
            shortWidth: { value: 0.15, min: 0.05, max: 0.5, step: 0.01 },
            shortHeight: { value: 0.6, min: 0.3, max: 2, step: 0.1 },
        }),
        'Wild Grass': folder({
            wildCount: { value: 4000, min: 1000, max: 50000, step: 1000 },
            wildWidth: { value: 0.3, min: 0.1, max: 1.0, step: 0.05 },
            wildHeight: { value: 0.9, min: 0.5, max: 3, step: 0.1 },
        })
    });

    // Memoize types with counts to avoid unstable objects
    const grassTypesWithCounts = useMemo(() => {
        // Map Leva values to types
        const overrides = [
            { width: tallWidth, height: tallHeight, countVertices: tallCount },
            { width: shortWidth, height: shortHeight, countVertices: shortCount },
            { width: wildWidth, height: wildHeight, countVertices: wildCount },
        ];

        return GRASS_TYPES.map((type, index) => ({
            ...type,
            ...overrides[index],
            count: Math.floor((mobile ? overrides[index].countVertices * 0.5 : overrides[index].countVertices) / (CHUNKS_PER_ROW * CHUNKS_PER_ROW))
        }));
    }, [mobile, tallCount, tallWidth, tallHeight, shortCount, shortWidth, shortHeight, wildCount, wildWidth, wildHeight]);

    return (
        <group>
            {grassTypesWithCounts.map((type, i) => (
                <group key={i}>
                    {chunks.map((chunk, j) => (
                        <GrassChunk
                            key={`${i}-${j}`}
                            config={type} // Stable object now
                            x={chunk.x}
                            z={chunk.z}
                            lowPerf={lowPerf}
                        />
                    ))}
                </group>
            ))}
        </group>
    );
};

// Memoized Chunk Component to prevent re-renders if props are stable
const GrassChunk = ({ config, x, z, lowPerf }: { config: any, x: number, z: number, lowPerf: boolean }) => {
    const meshRef = useRef<THREE.InstancedMesh>(null);
    const dummy = useMemo(() => new THREE.Object3D(), []);
    const material = useMemo(() => {
        const topColor = new THREE.Color(...config.colorTop);
        return new THREE.MeshStandardMaterial({
            color: topColor,
            side: THREE.DoubleSide,
            roughness: 0.85,
            metalness: 0.05,
        });
    }, [config]);

    // Deterministic Random Helper
    const seededRandom = (s: number) => {
        const x = Math.sin(s) * 10000;
        return x - Math.floor(x);
    };

    useLayoutEffect(() => {
        if (!meshRef.current) return;

        let idx = 0;
        const maxAttempts = config.count * 2;
        let seed = x * 12.9898 + z * 78.233 + config.count; // Simple dot product hash seed

        for (let i = 0; i < maxAttempts; i++) {
            if (idx >= config.count) break;

            // Pseudo-random position within chunk
            const r1 = seededRandom(seed++); // x offset
            const r2 = seededRandom(seed++); // z offset

            const lx = x + r1 * CHUNK_SIZE;
            const lz = z + r2 * CHUNK_SIZE;

            // Simple Circle Mask (radius ~200) to keep original map shape
            if (Math.sqrt(lx * lx + lz * lz) > 200) continue;

            if (isRoad(lx, lz) || isRiver(lx, lz) || isObject(lx, lz)) continue;

            const ly = getHeight(lx, lz);
            if (!Number.isFinite(ly)) continue;

            const rScale = seededRandom(seed++);
            const rRot = seededRandom(seed++);
            if (!Number.isFinite(rScale) || !Number.isFinite(rRot)) continue;

            // Lower grass slightly to prevent floating on hills
            dummy.position.set(lx, ly - 0.15, lz);
            dummy.scale.setScalar(0.7 + rScale * 0.6);
            dummy.rotation.y = rRot * Math.PI;
            if (!Number.isFinite(dummy.position.y) || !Number.isFinite(dummy.scale.x) || !Number.isFinite(dummy.rotation.y)) {
                continue;
            }
            dummy.updateMatrix();

            meshRef.current.setMatrixAt(idx++, dummy.matrix);
        }
        meshRef.current.count = idx;
        meshRef.current.instanceMatrix.needsUpdate = true;

        if (meshRef.current.geometry.boundingSphere) {
            meshRef.current.computeBoundingSphere();
        }

    }, [config, x, z]); // config is now stable from useMemo in parent

    return (
        <instancedMesh
            ref={meshRef}
            args={[undefined, undefined, config.count]}
            receiveShadow={!lowPerf}
            castShadow={!lowPerf}
            frustumCulled={true}
        >
            <planeGeometry
                args={[config.width, config.height, 1, config.segments]}
                onUpdate={(geo) => geo.translate(0, config.height / 2, 0)}
            />
            <primitive object={material} attach="material" />
        </instancedMesh>
    );
}
